import React, { useEffect, useState } from "react";
import { Eye } from "@phosphor-icons/react";
import api from "@/lib/api";
import { publicLink } from "@/lib/landerDomain";
import { useSharedSections } from "@/lib/useSharedSections";
import { AdminModal } from "@/components/pageSettings/AdminModal";
import { DesignPreview, sampleCtx } from "./designPreview";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? null : u.startsWith("http") ? u : `${BACKEND}${u}`);

function classicUrl(profile) {
  const q = new URLSearchParams({ preview: "1", ty_design: "classic", firm: profile.firm_name || "Your firm" });
  if (profile.phone) q.set("phone", profile.phone);
  return publicLink(`/lp/thank-you?${q}`);
}

function DesignCard({ d, inUse, onPreview, p }) {
  const tid = d.key.replace(":", "-");
  return (
    <div className={`flex flex-col gap-1.5 border-2 border-black p-2 ${inUse ? "bg-emerald-50" : "bg-white"}`} data-testid={`${p}-card-${tid}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-chivo text-sm font-black leading-tight">{d.name}</span>
        {inUse && <span className="border border-black bg-emerald-600 px-1 text-[9px] uppercase tracking-widest text-white" data-testid={`${p}-in-use-${tid}`}>In use</span>}
      </div>
      {d.description && <p className="text-[10.5px] leading-snug text-zinc-600">{d.description}</p>}
      {onPreview ? (
        <button type="button" onClick={onPreview} data-testid={`${p}-preview-${tid}`}
                className="mt-auto inline-flex w-fit items-center gap-1 border border-black px-2 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100 hover:bg-black hover:text-white">
          <Eye size={11} /> Preview
        </button>
      ) : <span className="mt-auto text-[10px] text-zinc-500" data-testid={`${p}-empty-${tid}`}>Not built yet</span>}
    </div>
  );
}

// View-only list of every thank-you design, previewed with the buyer's own details. Admins edit designs.
export function BuyerThankYouDesigns({ profile }) {
  const shared = useSharedSections();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get(`/thankyou-tests/page/${profile.id}/designs`).then((r) => setData(r.data)).catch(() => setData({ designs: [] }));
  }, [profile.id]);
  if (!data?.designs?.length) return null;
  const p = `buyer-ty-designs-${profile.id}`;
  const ctx = sampleCtx(shared, {
    firm: profile.firm_name, phone: profile.phone, logoUrl: absUrl(profile.logo_url), accent: profile.brand_color || undefined,
    contact: data.contact, videoUrl: data.video_url,
  });
  return (
    <section className="mb-4" data-testid={p}>
      <div className="font-chivo font-black">Your thank-you page designs</div>
      <p className="mb-2 text-[11px] text-zinc-600">
        The page visitors see after they send their case. Preview any design with your details. Your account manager edits these pages.
      </p>
      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
        {data.designs.map((d) => <DesignCard key={d.key} d={d} inUse={d.key === data.current} onPreview={d.key === "classic" || d.blocks?.length ? () => setOpen(d) : null} p={p} />)}
      </div>
      {open && (
        <AdminModal eyebrow="Thank-you page preview" title={open.name} onClose={() => setOpen(null)} testId={`${p}-modal`} wide>
          {open.blocks?.length ? <DesignPreview blocks={open.blocks} ctx={ctx} note="Preview with your details" /> : (
            <iframe title="Classic thank-you page" src={classicUrl(profile)} className="h-[72vh] w-full border-2 border-black" data-testid={`${p}-classic-frame`} />
          )}
        </AdminModal>
      )}
    </section>
  );
}
